import {create} from '@github-ui/dotcom-schema/protobuf'
import {ReviewerSchema, type Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'
import {ReviewState} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import styles from './Reviewers.module.css'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ReviewerIdentity} from './ReviewerIdentity'

import {useReviewers} from '../data/use-reviewers'
import {SidebarSection} from '../SidebarSection'
import {reviewState} from './review-state-helpers'
import {computeTeamCompositeStates, type TeamCompositeState} from './team-review-state-helpers'

export function TeamReviewSummary() {
  return <TeamReviewSummaryComponent />
}

export function TeamReviewSummaryComponent() {
  const {data: reviewers} = useReviewers()
  if (!reviewers) return null

  const teamStates = computeTeamCompositeStates(reviewers)

  if (teamStates.length === 0) {
    return null
  }

  return (
    <SidebarSection title="Team review summary">
      <ErrorBoundary
        boundaryName="TeamReviewSummaryComponent"
        fallback={<span className={styles.emptyState}>Unable to load team reviews</span>}
      >
        <ul className={styles.reviewerList}>
          {teamStates.map(team => (
            <TeamCompositeRow key={team.teamName} team={team} />
          ))}
        </ul>
      </ErrorBoundary>
    </SidebarSection>
  )
}

function TeamCompositeRow({team}: {team: TeamCompositeState}) {
  const {icon: stateIcon, className: stateClass} = reviewState(team.compositeState)
  const syntheticReviewer: Reviewer =
    team.teamReviewer ?? create(ReviewerSchema, {displayLogin: team.teamName, reviewerType: 'Team'})
  const hasAvatar = syntheticReviewer.avatarUrl !== ''
  const isPending = team.compositeState === ReviewState.PENDING
  const nestedUsers = (
    isPending ? [...team.completedUserReviews, ...team.pendingUserRequests] : team.completedUserReviews
  ).flatMap(r => (r.reviewer ? [r.reviewer] : []))

  return (
    <>
      <li className={styles.reviewerRow}>
        {hasAvatar ? (
          <ReviewerIdentity reviewer={syntheticReviewer} />
        ) : (
          <span className={styles.reviewerIdentity}>
            <span className={styles.reviewerName}>{team.teamName}</span>
          </span>
        )}
        <span className={styles.indicators}>
          <IconButton
            icon={stateIcon}
            aria-label={`${team.teamName}: ${team.stateLabel}`}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, stateClass)}
          />
        </span>
      </li>
      {nestedUsers.length > 0 && (
        <li className={clsx(styles.reviewerRow, styles.nestedReviewerItem)}>
          <span className={clsx(styles.connector, styles.connectorLast)} aria-hidden="true" />
          <span className={styles.nestedUserList}>
            {nestedUsers.map(reviewer => (
              <span key={reviewer.id.toString()} className={styles.nestedUserEntry}>
                <ReviewerIdentity reviewer={reviewer} />
              </span>
            ))}
          </span>
        </li>
      )}
    </>
  )
}
